import { uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useRef } from 'react'
import { secToHourMinute } from '../../../utils/date'

interface Props {
  list: {
    label: string
    value: number
  }[]
  chartSize: number
  color?: string
  strongColor?: string
  type?: string
  gap?: boolean
  ruler?: boolean
  scale?: boolean
  labelSkip?: number
  vLabelLimit?: number
}

const LineChart: FC<Props> = (props) => {
  const {
    list,
    chartSize,
    color,
    strongColor,
    type,
    gap,
    ruler,
    scale,
    labelSkip,
    vLabelLimit,
  } = props

  const V_LABEL_LIMIT = 3

  const graphRef = useRef<HTMLCanvasElement>(null)

  const maxValue = useMemo(() => (
    Math.max(...list.map((value) => value.value))
  ), [list])

  const buildUnit = (value: number) => {
    if (!value) return <></>
    if (type === "person") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          人
        </>
      )
    }
    if (type === "biz") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          biz
        </>
      )
    }
    if (type === "page") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          枚
        </>
      )
    }
    if (type === "time") {
      return (
        <span>
          {secToHourMinute(value)}
        </span>
      )
    }
    if (type === "times") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          回
        </>
      )
    }
    if (type === "percent") {
      return (
        <>
          <span>
            {value.toLocaleString()}
          </span>
          %
        </>
      )
    }
    return (
      <></>
    )
  }

  useEffect(() => {
    const width = graphRef.current?.clientWidth || 0
    const height = graphRef.current?.clientHeight || 0
    const canvas = graphRef.current
    if (!canvas || !canvas.getContext) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const max = Math.max(...list.map((value) => value.value))
    canvas.width = width
    canvas.height = height
    ctx.beginPath()
    ctx.lineWidth = 1
    ctx.strokeStyle = color || "#008ffb"
    list.forEach((value, index) => {
      ctx.lineTo(
        (width / (list.length - 1)) * index,
        height - ((value.value / max) * height) ? height - ((value.value / max) * height) - 1 : 0,
      )
    })
    ctx.stroke()
  }, [location])

  return (
    <div className="line_chart_wrap">
      <div className={`vertical ${(ruler && type) ? "ruler" : ""}`}>
        <div
          className="line_chart_area"
          style={{
            height: `${chartSize}px`,
            justifyContent: gap ? "space-evenly" : "center",
          }}
        >
          {
            scale && Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
              <div
                className="scale"
                style={{
                  top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}))`,
                }}
                key={uniqueId("ruler")}
              />
            ))
          }
          <canvas
            ref={graphRef}
            style={{
              height: `${chartSize}px`,
              width: `calc(100% - ${100 / list.length}%)`,
              zIndex: 1,
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 15px)",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            textAlign: "center",
          }}
        >
          {
            list.map((value, index) => (
              <div
                style={{
                  position: "relative",
                  width: `calc(100% / ${list.length || 1})`,
                }}
                key={uniqueId("line_")}
              >
                {
                  ruler ? (
                    <div
                      style={{
                        width: "1px",
                        height: "6px",
                        backgroundColor: "#CCCCCC",
                        position: "absolute",
                        top: "-15px",
                        left: "50%",
                        transform: "translateX(-50%)",
                      }}
                    />
                  ) : (
                    <></>
                  )
                }
                {
                  labelSkip && (index % labelSkip) !== 0 ? (
                    <></>
                  ) : (
                    <div>
                      {value.label}
                    </div>
                  )
                }
                <div
                  style={{
                    position: "absolute",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: strongColor,
                    left: "50%",
                    top: `-${(chartSize * (value.value / maxValue)) + 15}px`,
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: `-${(chartSize * (value.value / maxValue)) + 30}px`,
                    transform: "translate(-50%, -50%)",
                    color: "#666666",
                    zIndex: 1,
                  }}
                >
                  {buildUnit(value.value)}
                </div>
              </div>
            ))
          }
        </div>
        {
          ruler ? (
            <div className="ruler_area">
              <div
                className="horizontal_ruler_area"
                style={{
                  top: `${chartSize}px`,
                }}
              />
              {
                type ? (
                  <div className="vertical_ruler_area">
                    <div className="label_area">
                      {
                        Array((vLabelLimit || V_LABEL_LIMIT)).fill(null).map((_v, i) => (
                          <React.Fragment key={uniqueId("ruler")}>
                            <div
                              className="scale"
                              style={{
                                top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                              }}
                            />
                            <div
                              className="label"
                              style={{
                                top: `calc((${chartSize}px / ${vLabelLimit || V_LABEL_LIMIT} * ${i}) + 8px)`,
                              }}
                            >
                              {buildUnit(Math.floor((maxValue / (vLabelLimit || V_LABEL_LIMIT)) * ((vLabelLimit || V_LABEL_LIMIT) - i)))}
                            </div>
                          </React.Fragment>
                        ))
                      }
                    </div>
                  </div>
                ) : (
                  <></>
                )
              }
            </div>
          ) : (
            <></>
          )
        }
      </div>
    </div>
  )
}

LineChart.defaultProps = {
  color: "#008ffb",
  strongColor: "#000000",
  type: "",
  gap: true,
  ruler: false,
  scale: false,
  labelSkip: 0,
  vLabelLimit: 3,
}

export default LineChart
